/**
 * The before-hire panel (queue item 219, phase 2): the requisition, the application's
 * workflow steps (days in each against the peer median), the offer against the first
 * paycheck, and the onboarding with its open tasks. Data: sections.pre_hire -- the section
 * and each of its parts either carry data or say why not (services/lifeline_pre_hire).
 */
import { fmtDate, fmtDays, fmtMoney, offerCheckText, preHireParts } from './lifelineModel'

function Fact({ label, children }) {
  return (
    <div className="ll-fact">
      <div className="ll-eyebrow">{label}</div>
      <div className="ll-fact-v">{children ?? '—'}</div>
    </div>
  )
}

function Missing({ what, part }) {
  return <p className="ll-na"><b>{what} not available.</b> {part?.reason || 'No data.'}</p>
}

function vsMedian(days, median) {
  if (days == null) return null
  if (median == null) return fmtDays(days)
  return `${fmtDays(days)} · median ${fmtDays(median)}`
}

function Steps({ steps }) {
  const shown = (steps || []).filter(s => s.days != null)
  const max = Math.max(1, ...shown.map(s => Math.max(s.days, s.median_days || 0)))
  return (
    <div className="ll-table-wrap">
      <table className="ll-table ll-steps">
        <caption className="ll-note" style={{ textAlign: 'left', captionSide: 'top', paddingBottom: 4 }}>
          Days in each step · bar = this hire, tick = median for the same step in the same hiring process
        </caption>
        <thead><tr><th>Step</th><th>Entered</th><th className="num">Days</th><th className="num">Median</th><th aria-hidden="true" /></tr></thead>
        <tbody>
          {(steps || []).map((s, i) => {
            const slow = s.days != null && s.median_days != null && s.days > s.median_days
            return (
              <tr key={i}>
                <td style={{ whiteSpace: 'normal' }}>{s.name}{s.left_on ? '' : <span className="ll-note"> · current</span>}</td>
                <td className="ll-mono">{fmtDate(s.entered_on)}</td>
                <td className="ll-mono num" style={slow ? { color: 'var(--ll-medium)', fontWeight: 600 } : undefined}>{s.days != null ? s.days : '—'}</td>
                <td className="ll-mono num">{s.median_days != null ? s.median_days : '—'}</td>
                <td aria-hidden="true" style={{ width: '38%', minWidth: 80 }}>
                  {s.days != null && (
                    <span className="ll-step-track">
                      <span className="ll-step-bar" style={{ width: `${(s.days / max) * 100}%`, background: slow ? 'var(--ll-medium)' : 'var(--ll-recruiting)' }} />
                      {s.median_days != null && <span className="ll-step-median" style={{ left: `${(s.median_days / max) * 100}%` }} />}
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function OfferCheck({ offer }) {
  const v = offer?.vs_first_check
  if (!v) return null
  if (!v.available) return <p className="ll-na"><b>Offer vs first paycheck not compared.</b> {v.reason}</p>
  return (
    <div className={`ll-offer-check${v.differs ? ' ll-offer-check--differs' : ''}`} role="status">
      {v.differs && <span className="ll-flag">Offer ≠ first paycheck</span>}
      <span>{offerCheckText(offer)}</span>
      {v.first_check && v.compared_with !== 'first_check_rate' && (
        <span className="ll-note">First check {fmtDate(v.first_check.pay_date)} at {fmtMoney(v.first_check.pay_rate)}/h. {v.note}</span>
      )}
      {v.first_check && v.compared_with === 'first_check_rate' && (
        <span className="ll-note">First check paid {fmtDate(v.first_check.pay_date)}.</span>
      )}
    </div>
  )
}

export default function PreHire({ section }) {
  if (!section?.available) {
    return (
      <section className="ll-card">
        <div className="ll-eyebrow" style={{ color: 'var(--ll-recruiting)' }}>Before hire</div>
        <p className="ll-na"><b>Before-hire record not available.</b> {section?.reason}</p>
      </section>
    )
  }
  const { requisition: rq, application: app, offer, onboarding: onb } = preHireParts(section)
  const others = (section.hires || []).filter(h => h.hire_date !== section.hire_date || h.requisition?.number !== rq?.number)
  const rate = offer?.rate != null ? (offer.rate_basis === 'annual' ? `${fmtMoney(offer.rate, 0)}/yr` : `${fmtMoney(offer.rate)}/h`) : null
  return (
    <section className="ll-card ll-card--wide ll-prehire" aria-label="Before hire">
      <div className="ll-eyebrow" style={{ color: 'var(--ll-recruiting)' }}>
        Before hire · hired {fmtDate(section.hire_date)}{section.internal ? ' · internal applicant' : ''}
      </div>
      <h2>{rq?.title || 'Requisition'}{rq?.number ? <span className="ll-mono ll-note"> · {rq.number}</span> : null}</h2>

      <div className="ll-prehire-cols">
      <div className="ll-prehire-col">
      {rq ? (
        <div className="ll-detail-grid">
          <Fact label="Opened">{fmtDate(rq.opened_on)}</Fact>
          <Fact label="Hiring manager">{rq.hiring_manager}</Fact>
          <Fact label="Location">{rq.location}</Fact>
          <Fact label="Org unit">{rq.org}</Fact>
          <Fact label="Applicants · hires"><span className="ll-mono">{rq.applicants} · {rq.hires}</span></Fact>
          <Fact label="Requisition to hire"><span className="ll-mono">{vsMedian(section.days_requisition_to_hire, section.median_days_requisition_to_hire)}</span></Fact>
        </div>
      ) : <Missing what="Requisition" part={section.requisition} />}

      <h3 className="ll-h3">Application</h3>
      {app ? (
        <>
          <div className="ll-detail-grid">
            <Fact label="Applied">{fmtDate(app.applied_on)}</Fact>
            <Fact label="Source">{app.source}{app.internal ? ' · internal' : ''}</Fact>
            <Fact label="Applied to hire"><span className="ll-mono">{vsMedian(app.days_to_hire, app.median_days_to_hire)}</span></Fact>
          </div>
          <Steps steps={app.steps} />
        </>
      ) : <Missing what="Application" part={section.application} />}
      </div>
      <div className="ll-prehire-col">

      <h3 className="ll-h3">Offer</h3>
      {offer ? (
        <>
          <div className="ll-detail-grid">
            <Fact label="Sent">{fmtDate(offer.sent_on)}</Fact>
            <Fact label="Accepted">{fmtDate(offer.accepted_on)}</Fact>
            <Fact label="Offered rate"><span className="ll-mono">{rate}</span></Fact>
            {offer.offers > 1 && <Fact label="Offers made">{offer.offers}</Fact>}
          </div>
          <OfferCheck offer={offer} />
        </>
      ) : <Missing what="Offer" part={section.offer} />}

      <h3 className="ll-h3" style={{ color: 'var(--ll-onboarding)' }}>Onboarding</h3>
      {onb ? (
        <>
          <div className="ll-detail-grid">
            <Fact label="Started">{fmtDate(onb.started_on)}</Fact>
            <Fact label="Completed">{onb.completed_on ? fmtDate(onb.completed_on) : `Not yet${onb.status ? ` (${onb.status})` : ''}`}</Fact>
            <Fact label="Tasks done"><span className="ll-mono">{onb.tasks_total != null ? `${onb.tasks_done} of ${onb.tasks_total}` : '—'}</span></Fact>
          </div>
          {onb.open_tasks?.length > 0 && (
            <ul className="ll-open-tasks">
              {onb.open_tasks.map((t, i) => <li key={i}>{t.name}{t.assigned_on ? <span className="ll-note"> · assigned {fmtDate(t.assigned_on)}</span> : null}</li>)}
            </ul>
          )}
          {onb.note && <p className="ll-note">{onb.note}</p>}
        </>
      ) : <Missing what="Onboarding" part={section.onboarding} />}
      </div>
      </div>

      {others.length > 0 && (
        <p className="ll-note">
          Also hired through recruiting: {others.map(h => `${h.requisition?.title || 'requisition'}${h.requisition?.number ? ` (${h.requisition.number})` : ''}, ${fmtDate(h.hire_date)}`).join('; ')}.
        </p>
      )}
    </section>
  )
}
